import { useTasksStore } from "@/stores/v2";
import { ChallengeRevision } from "@/types/v2/task/challenge/history.types"
import { WeightRecord } from "@/types/v2";
import { storeToRefs } from "pinia";
import { isChallenge } from "@/stores/v2/tasks/utils";
import { ChallengeProgressEntry } from "@/types/v2/task/challenge/configuration.types";

export function useRecord() {
  // ===================================================================
  // Store
  // ===================================================================
  const { activeTask } = storeToRefs(useTasksStore());

  // ===================================================================
  // Actions
  // ===================================================================
  // Save Challenge Record
  const createChallengeRecord = (configKey: string, data: ChallengeProgressEntry) => {
    if(!activeTask.value || !isChallenge(activeTask.value)) return;

    const configuration = activeTask.value.details.configurations.find(
      (config) => config.key === configKey
    );

    if (!configuration) return;

    if (!configuration.progressData) {
      configuration.progressData = []
    }

    configuration.progressData.push(data);
  }

  // Save Individual Configuration Weight
  const createWeightRecord = (data: WeightRecord) => {
    if(!activeTask.value || !isChallenge(activeTask.value)) return;

    if (!activeTask.value.details.weightConfiguration.progressData) {
      activeTask.value.details.weightConfiguration.progressData = [];
    }

    activeTask.value.details.weightConfiguration.progressData.push(data);
  }

  // Update Challenge Record
  const updateChallengeRecord = (configKey: string, entryIndex: number, updatedData: Partial<ChallengeProgressEntry>): boolean => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return false;

    const configuration = activeTask.value.details.configurations.find(
      (config) => config.key === configKey
    );

    if (!configuration || !configuration.progressData || !configuration.progressData[entryIndex]) return false;

    // Update the entry with new values
    Object.assign(configuration.progressData[entryIndex], updatedData);

    return true;
  };

  const addRevisionToRecord = (
    configKey: string,
    index: number,
    currentData: Partial<ChallengeProgressEntry>,
    oldData: Partial<ChallengeProgressEntry>,
    justification: string
  ): boolean => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return false;

    // Check if configurations exist
    if (!activeTask.value.details?.configurations) {
      console.error('No configurations found in active task');
      return false;
    }

    // Find the configuration index
    const configIndex = activeTask.value.details.configurations.findIndex(
      config => config.key === configKey
    );

    if (configIndex === -1) {
      console.error(`Configuration with key ${configKey} not found`);
      return false;
    }

    // Check if the configuration and progressData exist
    if (!activeTask.value.details.configurations[configIndex]) {
      return false;
    }

    // Initialize progressData if needed
    if (!activeTask.value.details.configurations[configIndex].progressData) {
      activeTask.value.details.configurations[configIndex].progressData = [];
    }

    // Check if entry exists
    const progressData = activeTask.value.details.configurations[configIndex].progressData;
    if (!progressData || index < 0 || index >= progressData.length) {
      console.error(`Entry at index ${index} not found`);
      return false;
    }

    // Create the revision
    const revision: ChallengeRevision = {
      challenged: oldData.challenged !== undefined ? oldData.challenged : null,
      weight: oldData.weight !== undefined ? oldData.weight : null,
      comment: justification,
      timestamp: new Date().toISOString()
    };

    // Get a reference to the entry we want to modify
    const entry = progressData[index];

    // Initialize revisions array if needed (directly on the entry)
    if (!entry.revisions) {
      entry.revisions = [];
    }

    // Add revision to the entry (this directly modifies the state)
    entry.revisions.push(revision);

    // Update the entry data (also directly modifies the state)
    Object.assign(entry, currentData);

    return true;
  };

  // ===================================================================
  // Getters
  // ===================================================================
  // Get Challenge Records by Configuration Key
  const getChallengeRecordsByConfigKey = (key: string): ChallengeProgressEntry[] | [] => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];

    const configuration = activeTask.value.details.configurations.find((config) => {
      return config.key === key
    })

    if (!configuration) return [];

    if (!configuration.progressData) {
      configuration.progressData = [];
    }

    return configuration.progressData;
  }


  return {
    // ===================================================================
    // Actions
    // ===================================================================
    createChallengeRecord,
    createWeightRecord,
    updateChallengeRecord,
    addRevisionToRecord,

    // ===================================================================
    // Getters
    // ===================================================================
    getChallengeRecordsByConfigKey,
  };
}